import { Entity, EntityProperty, PropertyType } from '../../types/builder';
import toast from 'react-hot-toast';

interface Props {
  entity: Entity;
  onUpdate: (entity: Entity) => void;
}

const TYPES: PropertyType[] = [
  'string', 'integer', 'decimal',
  'boolean', 'date', 'datetime', 'uuid'
];

const PropertiesTab = ({ entity, onUpdate }: Props) => {

  const addProperty = () => {

     if (entity.properties.length >= 30) {
    toast.error(
      'Maximum 30 properties per entity!'
    );
    return;
  }

    onUpdate({
      ...entity,
      properties: [
        ...entity.properties,
        {
          id: Date.now(),
          name: 'newProperty',
          type: 'string',
          required: false,
          pk: false
        }
      ]
    });
  };

  const deleteProperty = (id: number) => {
    const prop = entity.properties
      .find(p => p.id === id);
    if (prop?.pk) return;
    onUpdate({
      ...entity,
      properties: entity.properties
        .filter(p => p.id !== id)
    });
  };

    const updateProperty = (
    id: number,
    field: keyof EntityProperty,
    value: any,
    extraFields?: Partial<EntityProperty>
    ) => {
    onUpdate({
        ...entity,
        properties: entity.properties.map(p =>
        p.id === id
            ? { ...p, [field]: value, ...extraFields }
            : p
        )
    });
    };

  return (
    <div>
      {/* Header */}
      <div className="grid grid-cols-5 gap-2
                      pb-2 mb-1
                      border-b border-gray-800">
        {['Property Name','Type','Required','PK','']
          .map(h => (
            <span key={h}
                  className="text-gray-600
                             font-mono text-xs
                             uppercase
                             tracking-wider
                             text-center">
              {h}
            </span>
          ))}
      </div>

      {/* Property Rows */}
      {entity.properties.map(prop => (
        <div key={prop.id}
             className={`grid grid-cols-5
                   gap-2 py-1.5
                   items-center
                   rounded-lg px-1
                   transition-colors
                   ${entity.properties
                       .filter(p => p.pk).length > 1
                     && prop.pk
                       ? 'bg-red-500/5 border border-red-500/20'
                       : ''
                   }`}>

          {/* Name */}
          <input
            value={prop.name}
            onChange={e => updateProperty(
              prop.id, 'name', e.target.value
            )}
            className="bg-gray-800
                       border border-gray-700
                       text-gray-200
                       rounded-lg px-2 py-1.5
                       text-xs font-mono
                       outline-none
                       focus:border-blue-500
                       col-span-1"
            placeholder="propertyName"
          />

          {/* Type */}
          <select
            value={prop.type}
            onChange={e => updateProperty(
              prop.id, 'type', e.target.value
            )}
            className="bg-gray-800
                       border border-gray-700
                       text-gray-200
                       rounded-lg px-2 py-1.5
                       text-xs font-mono
                       outline-none">
            {TYPES.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

            {/* Required */}
            <div className="flex justify-center">
            <input
                type="checkbox"
                checked={prop.required}
                onChange={e =>
                updateProperty(
                    prop.id, 'required', e.target.checked
                )
                }
                className="accent-blue-500
                        w-4 h-4 cursor-pointer"
            />
            </div>

            {/* PK Validation Warning */}
            {entity.properties.filter(p => p.pk).length > 1 && (
            <div className="flex items-center gap-2
                            bg-red-500/10
                            border border-red-500/30
                            rounded-xl px-4 py-3 mb-3">
                <span className="text-red-400 text-lg">⚠️</span>
                <div>
                <p className="text-red-400 text-sm
                                font-semibold">
                    Multiple Primary Keys Selected!
                </p>
                <p className="text-red-400/70 text-xs mt-0.5">
                    Only one PK can exist per entity.
                    Please uncheck one.
                </p>
                </div>
            </div>
            )}

            {/* PK — with validation ✅ */}
            <div className="flex justify-center">
            <input
                type="checkbox"
                checked={prop.pk}
                onChange={e => {
                // If trying to SET as PK
                if (e.target.checked) {
                    // Check if another PK exists
                    const existingPk = entity.properties
                    .find(p => p.pk && p.id !== prop.id);

                    if (existingPk) {
                    toast.error(
                        `"${existingPk.name}" is already PK! Uncheck it first.`
                    );
                    return; // Block! ✅
                    }
                }

                // All good — update
                updateProperty(
                    prop.id, 'pk',
                    e.target.checked,
                    e.target.checked
                    ? { required: true }
                    : {}
                );
                }}
                className="accent-yellow-400
                        w-4 h-4 cursor-pointer"
            />
            </div>

          {/* Delete */}
          <div className="flex justify-center">
            <button
              onClick={() => deleteProperty(prop.id)}
              className="text-gray-600
                         hover:text-red-400
                         transition-colors text-sm">
              ✕
            </button>
          </div>
        </div>
      ))}

      {/* Add Property */}
      <button
        onClick={addProperty}
        disabled={entity.properties.length >= 30}
        className="w-full mt-3
                   border border-dashed
                   border-gray-700
                   hover:border-blue-500
                   text-gray-600
                   hover:text-blue-400
                   rounded-lg py-2
                   text-xs font-mono
                   transition-colors">
        + Add Property
        <span className="ml-2 text-gray-600">
            ({entity.properties.length}/30)
        </span>
      </button>
    </div>
  );
};

export default PropertiesTab;